"use client";

import * as React from "react";
import { Tooltip as RechartsTooltip } from "recharts";

import { cn } from "@/lib/utils";

export type ChartConfig = Record<
  string,
  {
    label?: string;
    color?: string;
  }
>;

const ChartContext = React.createContext<ChartConfig | undefined>(undefined);

export function useChartConfig() {
  return React.useContext(ChartContext);
}

export function ChartContainer({
  config,
  className,
  children,
}: {
  config: ChartConfig;
  className?: string;
  children: React.ReactNode;
}) {
  const cssVariables = React.useMemo(() => {
    const entries = Object.entries(config).map(([key, value]) => {
      const color = value.color ?? `hsl(var(--chart-${key}))`;
      return [`--color-${key}`, color];
    });
    return Object.fromEntries(entries) as React.CSSProperties;
  }, [config]);

  return (
    <div className={cn("text-xs", className)} style={cssVariables}>
      <ChartContext.Provider value={config}>{children}</ChartContext.Provider>
    </div>
  );
}

export type ChartTooltipContentProps<TValue = number, TName = string> = {
  active?: boolean;
  className?: string;
  config?: ChartConfig;
  label?: string | number;
  nameKey?: string;
  labelFormatter?: (label: string) => string;
  payload?: ReadonlyArray<{
    dataKey?: string | number;
    name?: TName;
    value?: TValue;
  }>;
};

export function ChartTooltipContent<TValue = number, TName = string>({
  active,
  payload,
  label,
  className,
  config: configOverride,
  labelFormatter,
}: ChartTooltipContentProps<TValue, TName>) {
  const contextConfig = useChartConfig();
  const config = configOverride ?? contextConfig;

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const displayLabel = labelFormatter ? labelFormatter(String(label)) : label;

  const visibleEntries = payload.filter(
    (item): item is NonNullable<typeof item> & { value: NonNullable<TValue> } =>
      item.value !== undefined && item.value !== null,
  );

  return (
    <div className={cn("rounded-md border bg-popover p-3 shadow-sm", className)}>
      <div className="text-xs font-medium text-muted-foreground">{displayLabel}</div>
      <div className="mt-2 space-y-1 text-sm">
        {visibleEntries.map((item, index) => {
          const key = item.dataKey?.toString() ?? index.toString();
          const name = config?.[key]?.label ?? (item.name != null ? String(item.name) : key);
          const value = item.value;
          return (
            <div key={key} className="flex items-center justify-between gap-6">
              <span className="text-muted-foreground">{name}</span>
              <span className="font-semibold">{typeof value === "number" ? value.toLocaleString() : String(value)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ChartTooltip(props: React.ComponentProps<typeof RechartsTooltip>) {
  return <RechartsTooltip cursor={false} wrapperStyle={{ outline: "none" }} {...props} />;
}
